export interface PhoneCountryOption {
	label: string;
	value: string;
}

export interface ParsedPhoneNumber {
	countryCode: string;
	localNumber: string;
}

export const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = [
	{ label: 'US/CA (+1)', value: '+1' },
	{ label: 'BR (+55)', value: '+55' },
	{ label: 'MX (+52)', value: '+52' },
	{ label: 'AR (+54)', value: '+54' },
	{ label: 'CL (+56)', value: '+56' },
	{ label: 'CO (+57)', value: '+57' },
	{ label: 'PE (+51)', value: '+51' },
	{ label: 'UY (+598)', value: '+598' },
	{ label: 'PY (+595)', value: '+595' },
	{ label: 'BO (+591)', value: '+591' },
	{ label: 'EC (+593)', value: '+593' },
	{ label: 'VE (+58)', value: '+58' },
	{ label: 'PT (+351)', value: '+351' },
	{ label: 'ES (+34)', value: '+34' },
	{ label: 'FR (+33)', value: '+33' },
	{ label: 'DE (+49)', value: '+49' },
	{ label: 'GB (+44)', value: '+44' },
	{ label: 'IE (+353)', value: '+353' },
	{ label: 'IT (+39)', value: '+39' },
	{ label: 'NL (+31)', value: '+31' },
	{ label: 'BE (+32)', value: '+32' },
	{ label: 'CH (+41)', value: '+41' },
	{ label: 'AU (+61)', value: '+61' },
	{ label: 'NZ (+64)', value: '+64' },
	{ label: 'JP (+81)', value: '+81' },
	{ label: 'KR (+82)', value: '+82' },
	{ label: 'CN (+86)', value: '+86' },
	{ label: 'IN (+91)', value: '+91' },
	{ label: 'ZA (+27)', value: '+27' }
];

export class PhoneNumberUtils {
	private static readonly sortedCountryOptions = [...PHONE_COUNTRY_OPTIONS].sort(
		(left, right) => right.value.length - left.value.length
	);

	static parseStoredPhoneNumber(phoneNumber?: string): ParsedPhoneNumber {
		const prefixStrippedPhoneNumber = String(phoneNumber || '')
			.trim()
			.replace(/^tel:/i, '');

		if (!prefixStrippedPhoneNumber) {
			return { countryCode: '', localNumber: '' };
		}

		const matchedCountry = PhoneNumberUtils.sortedCountryOptions.find(option =>
			prefixStrippedPhoneNumber.startsWith(option.value)
		);

		if (!matchedCountry) {
			return { countryCode: '', localNumber: prefixStrippedPhoneNumber };
		}

		const localNumber = prefixStrippedPhoneNumber
			.substring(matchedCountry.value.length)
			.replace(/^[\s()-]+/, '')
			.trim();

		return {
			countryCode: matchedCountry.value,
			localNumber
		};
	}

	static buildStoredPhoneNumber(countryCode?: string, localNumber?: string): string {
		const rawLocalNumber = String(localNumber || '').trim();
		const normalizedLocalNumber = rawLocalNumber.replace(/^tel:/i, '').trim();

		if (!normalizedLocalNumber) {
			return '';
		}

		if (PhoneNumberUtils.isInternationalNumber(normalizedLocalNumber)) {
			return normalizedLocalNumber;
		}

		const normalizedCountryCode = PhoneNumberUtils.normalizeCountryCode(countryCode);

		return normalizedCountryCode ? `${normalizedCountryCode} ${normalizedLocalNumber}` : normalizedLocalNumber;
	}

	static normalizeCountryCode(countryCode?: string): string {
		const normalizedCountryCode = String(countryCode || '').trim();

		if (!normalizedCountryCode) {
			return '';
		}

		return normalizedCountryCode.startsWith('+') ? normalizedCountryCode : `+${normalizedCountryCode}`;
	}

	static hasPhoneValue(phoneNumber?: string): boolean {
		return String(phoneNumber || '').trim().length > 0;
	}

	static hasCountryCode(countryCode?: string): boolean {
		return PhoneNumberUtils.normalizeCountryCode(countryCode).length > 0;
	}

	static isInternationalNumber(phoneNumber?: string): boolean {
		return String(phoneNumber || '')
			.trim()
			.startsWith('+');
	}
}
